import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, addUser, updateUser, deleteUser, getRoles } from '../api';
import { FiUser, FiX } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';

const emptyForm = { username: '', email: '', password: '', full_name: '', role_id: '', is_active: 1 };

export default function Users() {
  const { can } = usePermissions();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([getUsers(), getRoles()]);
      setUsers(uRes.data);
      setRoles(rRes.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load users');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: '', role_id: u.role_id || '' }); setError(''); setShowModal(true); };

  const handleSave = async () => {
    if (!form.username || !form.email) { setError('Username and email are required'); return; }
    if (!editing && !form.password) { setError('Password is required when creating a user'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await updateUser(editing.id, form);
      else await addUser(form);
      setShowModal(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save user');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await deleteUser(id);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete user');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiUser /> Admin Users</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Create staff accounts and assign them a role from Roles &amp; Permissions</p>
        </div>
        {can('roles_users', 'create') && <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Username</th><th>Full Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.username}</strong></td>
                  <td>{u.full_name || '—'}</td>
                  <td>{u.email}</td>
                  <td>{u.role === 'admin' ? <span className="badge badge-gold">Super Admin</span> : (u.role_name || <span className="badge badge-muted">No role</span>)}</td>
                  <td>{u.is_active ? <span className="badge badge-success">Active</span> : <span className="badge badge-danger">Inactive</span>}</td>
                  <td>
                    <div className="action-buttons">
                      {can('roles_users', 'edit') && <button className="btn btn-secondary btn-small" onClick={() => openEdit(u)}>Edit</button>}
                      {u.role !== 'admin' && can('roles_users', 'delete') && <button className="btn btn-danger btn-small" onClick={() => setDeleteConfirm(u.id)}>Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit User' : 'Add New User'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Username *</label>
                <input className="form-control" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Full Name</label>
                <input className="form-control" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Email *</label>
                <input type="email" className="form-control" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{editing ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" className="form-control" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Role</label>
                <select className="form-control" value={form.role_id} onChange={e => setForm(p => ({ ...p, role_id: e.target.value }))}>
                  <option value="">No role (no access until assigned)</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              {editing && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Status</label>
                  <select className="form-control" value={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: +e.target.value }))}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update User' : 'Add User')}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header"><h3>Confirm Delete</h3></div>
            <div className="modal-body"><p>Are you sure you want to delete this user account?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
